import CorvuDialog from '@corvu/dialog'
import { DialogShell } from '@forest/shared/solid'
import type { Accessor } from 'solid-js'
import { Show } from 'solid-js'
import { logNonFatal } from '~/lib/diagnostics'
import {
  detailOverlayAnimation,
  dialogPanelAnimation,
} from '~/lib/dialog-animation'
import type { StarterPack } from '~/services/starter-pack'
import type { ShopBalances } from '~/store/session-ledger'
import { BalancePills } from './balance-pills'
import { createDetailSlidePager, DetailSlidePager } from './detail-slide-pager'
import { DetailOwnedTag } from './detail-unlock-footer'
import { DialogCloseButton, DialogPrimaryButton } from './dialog-buttons'

// The starter-pack detail (GLOSSARY.md "Starter pack (新手禮包)"): a pager of
// one detail slide per content (GLOSSARY.md "Detail slide"), captioned with the
// showing slide's fixed caption, above the price button at the
// Shop IAP price — or the owned tag once the pack is owned. No title or
// description paragraph. Expiry never closes it: it stays open on the pack
// it opened with for as long as Shop holds that pack.
export function StarterPackDetail(props: {
  balances: Accessor<ShopBalances | undefined>
  onClose: () => void
  // The price button's tap: starts the purchase, with no confirmation.
  onPurchase: (pack: StarterPack) => void
  open: boolean
  pack: StarterPack
  // A purchase started here is in flight (GLOSSARY.md "Shop IAP processing
  // overlay"): the detail stays beneath the overlay and can be neither
  // dismissed nor bought again until the flow lets go of it.
  purchasePending?: boolean
}) {
  const pager = createDetailSlidePager({
    imageUrls: () => props.pack.detailImageUrls,
    open: () => props.open,
    resetKey: () => props.pack.product.id,
  })
  const caption = () => props.pack.detailCaptions[pager.index()] ?? ''

  const reportImageFailure = (slideIndex: number, url: string) => {
    logNonFatal('shop_starter_pack_detail_image_failed', {
      index: String(slideIndex),
      product_id: String(props.pack.product.id),
      url,
    })
  }

  return (
    <DialogShell
      open={props.open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !props.purchasePending) props.onClose()
      }}
      closeOnEscapeKeyDown={false}
      testId="starter-pack-detail"
      class={`z-30 flex w-[300px] flex-col items-center gap-6 p-6 text-center text-[#333] outline-none ${dialogPanelAnimation}`}
      overlayClass={detailOverlayAnimation}
      overlayContent={
        <BalancePills
          balances={props.balances()}
          class="fixed right-2 top-[calc(8px+var(--safe-area-top))] z-[31] duration-300 animate-in fade-in"
          inverted
          testIdPrefix="starter-pack-detail-"
        />
      }
    >
      <>
        <DialogCloseButton
          disabled={!props.open || props.purchasePending}
          onClose={() => props.onClose()}
        />

        {/* The swipe area is everything above the price button, so the
            button never doubles as a gesture target. */}
        <DetailSlidePager
          dotsPosition="below-copy"
          onImageFailure={reportImageFailure}
          pager={pager}
          testIdPrefix="starter-pack-detail"
        >
          <CorvuDialog.Label
            data-testid="starter-pack-detail-caption"
            class="mt-6 w-full text-base font-semibold text-ink"
          >
            {caption()}
          </CorvuDialog.Label>
        </DetailSlidePager>

        <Show
          when={!props.pack.owned}
          fallback={<DetailOwnedTag testId="starter-pack-detail-owned" />}
        >
          <DialogPrimaryButton
            data-testid="starter-pack-detail-purchase"
            disabled={props.purchasePending}
            onClick={() => props.onPurchase(props.pack)}
          >
            <span data-testid="starter-pack-detail-price">
              {props.pack.priceLocalized}
            </span>
          </DialogPrimaryButton>
        </Show>
      </>
    </DialogShell>
  )
}
